import { WidgetHeader } from 'legacy/lut/components/widgets/public/partner360/widgetHeader/WidgetHeader'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'
import { ModalDialog } from 'core-components/library/modalDialog'
import { IModalDialogProps } from 'core-components/library/modalDialog'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'
import {
  getGlobalPartnerActualsTypes,
  getGlobalPartnerDefaultActualsType,
} from 'partner360/widgets/gp360Administration/helpers/getGlobalPartnerActualsTypes'

import { TRANSLATION_KEYS } from '../../../../translations'
import { GlobalPartnerContentTypes } from '../globalPartnerDetailsContent/components/contentTypeButtons'
import { GLOBAL_PARTNER_FORM_KEY } from '../globalPartnerDetailsContent/configs'
import { GlobalPartnerDetailsContent } from '../globalPartnerDetailsContent/GlobalPartnerDetailsContent'

import { IGlobalPartnerDetailsModalProps } from './models/globalPartnerDetailsModal'

import styles from './GlobalPartnerDetailsModal.module.scss'

export const GlobalPartnerDetailsModal: FC<IGlobalPartnerDetailsModalProps> = ({
  initialGlobalPartnerFormData,
  globalPartnerType,
  fYear,
  onClose,
  valueType,
  onChangeValueType,
  globalPartnerCurrentFYearTargetsNet,
  globalPartnerCurrentFYearTargetsTNet,
  globalPartnerCurrentFYearTargetsUTNet,
  globalPartnerCurrentFYearTargetsNdp,
  hqPartnerId,
}) => {
  /** -------------------- State ----------------------- */
  const [localValueType, setLocalValueType] = useState<ActualsValueTypes | undefined>()
  const allowedTypes = getGlobalPartnerActualsTypes(globalPartnerType)
  const requestedValueType = valueType ?? localValueType
  const effectiveValueType =
    requestedValueType && allowedTypes.includes(requestedValueType)
      ? requestedValueType
      : getGlobalPartnerDefaultActualsType(globalPartnerType)
  const changeValueType = onChangeValueType ?? setLocalValueType
  const [modalSize, setModalSize] = useState<IModalDialogProps<unknown>['size']>('sm')

  /** -------------------- Hooks ----------------------- */
  const dispatch = useAppDispatch()

  const [{ formData, isValid }] = useGetFormData(
    GLOBAL_PARTNER_FORM_KEY,
    initialGlobalPartnerFormData
  )

  /** -------------------- Variables ----------------------- */
  const isNewGlobalPartner = useMemo(() => !formData.globalPartnerId, [formData.globalPartnerId])

  /** -------------------- Methods ----------------------- */
  const renderHeader = useCallback(() => {
    if (isNewGlobalPartner) {
      return <WidgetHeader>{TRANSLATION_KEYS.globalPartnerDetails.createTitle}</WidgetHeader>
    }
    return (
      <>
        <WidgetHeader>{TRANSLATION_KEYS.globalPartnerDetails.updateTitle}</WidgetHeader>
        <div className={styles.globalPartnerName}>
          {initialGlobalPartnerFormData.globalPartnerName}
        </div>
      </>
    )
  }, [isNewGlobalPartner, initialGlobalPartnerFormData.globalPartnerName])

  const handleClose = useCallback(() => {
    dispatch(resetAllModifiedDataByKey(GLOBAL_PARTNER_FORM_KEY))
    onClose()
  }, [onClose, dispatch])

  const changeModalSize = useCallback((tab: GlobalPartnerContentTypes) => {
    if (tab === GlobalPartnerContentTypes.BasicInfo) {
      setModalSize('md')
    } else {
      setModalSize('xl')
    }
  }, [])

  useEffect(() => {
    if (!isNewGlobalPartner) {
      setModalSize('xl')
    }
  }, [isNewGlobalPartner])

  return (
    <ModalDialog
      show
      closeOnOutsideClick={false}
      onClose={handleClose}
      header={renderHeader}
      classes={{
        body: styles.bodyContent,
      }}
      size={modalSize}
      content={GlobalPartnerDetailsContent}
      contentProps={{
        isNewGlobalPartner,
        isValid,
        initialData: initialGlobalPartnerFormData,
        formData,
        globalPartnerType,
        fYear,
        onClose: handleClose,
        onTabChange: changeModalSize,
        valueType: effectiveValueType,
        onChangeValueType: changeValueType,
        globalPartnerCurrentFYearTargetsNet,
        globalPartnerCurrentFYearTargetsTNet,
        globalPartnerCurrentFYearTargetsUTNet,
        globalPartnerCurrentFYearTargetsNdp,
        hqPartnerId,
      }}
    />
  )
}
